/**
 * PWA Install Prompt (Solid.js)
 */
import { createSignal, onMount, onCleanup, Show } from 'solid-js';
import { useLocation } from '@solidjs/router';
import { useTranslation } from '../context/LanguageContext';
import { isMobileDevice, isStandalone } from '../utils/deviceDetection';
import { storage } from '../utils/storage';
import { canInstallNatively, promptInstall } from '../utils/pwaInstall';
import { css } from '../../styled-system/css';

interface PWAInstallPromptProps {
  onInstall?: () => void;
  onDismiss?: () => void;
}

/** Seiten mit eigener Install-Leiste (PWAInstallBanner) bzw. Anleitung – dort keinen zweiten Prompt stapeln */
const PATHS_WITH_OWN_INSTALL_HINT = ['/', '/login', '/pwa-install'];

export default function PWAInstallPrompt(props: PWAInstallPromptProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const onPageWithOwnHint = () => PATHS_WITH_OWN_INSTALL_HINT.includes(location.pathname);
  const [showPrompt, setShowPrompt] = createSignal(false);
  const [isStandaloneMode, setIsStandaloneMode] = createSignal(false);
  let dismissedTime = 0;

  onMount(() => {
    const standalone = isStandalone();
    const mobile = isMobileDevice();
    setIsStandaloneMode(standalone);

    if (standalone) {
      setShowPrompt(false);
      return;
    }

    const dismissed = storage.getItem('pwa-install-dismissed');
    dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);

    if (!standalone && mobile && dismissedTime < oneDayAgo) {
      setShowPrompt(true);
    } else {
      setShowPrompt(false);
    }

    const checkStandaloneInterval = setInterval(() => {
      const currentStandalone = isStandalone();
      if (currentStandalone !== standalone) {
        setIsStandaloneMode(currentStandalone);
        setShowPrompt(false);
      }
    }, 1000);

    onCleanup(() => {
      clearInterval(checkStandaloneInterval);
    });
  });

  const handleInstall = async () => {
    if (props.onInstall) {
      props.onInstall();
      return;
    }

    if (canInstallNatively()) {
      const outcome = await promptInstall();
      if (outcome) setShowPrompt(false);
      if (outcome === 'dismissed') storage.setItem('pwa-install-dismissed', String(Date.now()));
    } else {
      window.location.href = '/pwa-install';
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    storage.setItem('pwa-install-dismissed', String(Date.now()));
    setShowPrompt(false);
    if (props.onDismiss) props.onDismiss();
  };

  const promptStyle = css({
    position: 'fixed',
    bottom: '1rem',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 'zIndex.modal',
    background: 'linear-gradient(135deg, #2c1810 0%, #3d2415 100%)',
    border: '2px solid #8b4513',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    maxWidth: '400px',
    width: '90%',
    animation: 'slideUp 0.3s ease-out',
  });

  const contentStyled = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  });

  const titleStyle = css({
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#ffa500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: 0,
  });

  const descStyle = css({
    fontSize: '0.9rem',
    color: '#f5e6d3',
    lineHeight: 1.5,
    margin: 0,
  });

  const buttonsStyle = css({
    display: 'flex',
    gap: '0.75rem',
    marginTop: '0.5rem',
  });

  const btnBase = css({
    flex: 1,
    padding: '0.625rem 1rem',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '0.9rem',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.2s',
  });

  const btnInstall = css({
    background: 'linear-gradient(135deg, #ff8c00, #ffa500)',
    color: 'white',
    _hover: {
      opacity: 0.9,
      transform: 'translateY(-1px)',
    },
  });

  const btnDismiss = css({
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#f5e6d3',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    _hover: {
      background: 'rgba(255, 255, 255, 0.15)',
    },
  });

  return (
    <Show when={showPrompt() && !isStandaloneMode() && !onPageWithOwnHint()}>
      <div class={promptStyle}>
        <div class={contentStyled}>
          <h3 class={titleStyle}>
            <span>📱</span>
            <span>{t('pwa.installTitle') || 'KingofAnts installieren'}</span>
          </h3>
          <p class={descStyle}>
            {t('pwa.installDescription')}
          </p>
          <div class={buttonsStyle}>
            <button class={`${btnBase} ${btnInstall}`} onClick={handleInstall}>
              {t('pwa.install') || 'Installieren'}
            </button>
            <button class={`${btnBase} ${btnDismiss}`} onClick={handleDismiss}>
              {t('pwa.dismiss')}
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
